export const umlautMap: { [key: string]: string } = {
  Ü: 'UE',
  Ä: 'AE',
  Ö: 'OE',
  ü: 'ue',
  ä: 'ae',
  ö: 'oe',
  ß: 'ss',
};

const groupBy = (collection, iteratee): any[] => {
  const grouped: any = {};

  for (const item of collection) {
    if (item[iteratee] === undefined) {
      continue;
    }

    if (!grouped[item[iteratee]]) {
      grouped[item[iteratee]] = [];
    }

    grouped[item[iteratee]].push(item);
  }

  return grouped;
};

const isEqual = (a: any, b: any) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

const last = (arr) => {
  if (!arr || arr.length === undefined || !isFinite(arr.length)) {
    return undefined;
  }

  return arr[arr.length - 1];
};

const round = (num: number, precision = 0) => {
  const p = Math.pow(10, precision);
  return Math.round(num * p) / p;
};

interface DebounceOptions {
  leading?: boolean;
  trailing?: boolean;
  // maxWait? : number;
}

const debounce = (func: any, wait: number = 0, options?: DebounceOptions) => {
  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }

  let timeout: any;
  let leading = false;
  let trailing = true;
  let needsToBeCalled = false;

  if (typeof options === 'object' && options !== null) {
    leading = !!options.leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  return (...args) => {
    needsToBeCalled = true;

    const caller = () => {
      if (!needsToBeCalled) {
        return;
      }

      needsToBeCalled = false;
      func.apply(null, args);
    };

    if (leading && !timeout) {
      caller();
    }

    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      if (!trailing) {
        return;
      }

      caller();
    }, wait);
  };
};

function throttle(func: any, wait: number, options?: DebounceOptions) {
  let context, args, result;
  let timeout: any = null;
  let previous = 0;

  let leading = false;
  let trailing = true;

  if (options) {
    leading = !!options.leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  const later = () => {
    previous = leading === false ? 0 : Date.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) {
      context = args = null;
    }
  };

  return function (...fnArgs) {
    const now = Date.now();
    args = fnArgs;

    if (!previous && leading === false) {
      previous = now;
    }

    const remaining = wait - (now - previous);
    context = this;

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(context, args);
      if (!timeout) {
        context = args = null;
      }
    } else if (!timeout && trailing !== false) {
      timeout = setTimeout(later, remaining);
    }

    return result;
  };
}

const omit = (src: any, paths: string | string[]) => {
  const p = typeof paths === 'string' ? [paths] : paths;
  const keys = Object.keys(src);
  const result: any = {};

  for (const key of keys) {
    if (p.indexOf(key) > -1) {
      continue;
    }

    result[key] = src[key];
  }

  return result;
};

const max = (numbers: Date[] | number[]) => Math.max.apply(null, numbers);
const escapeStringRegexp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
/**
 * Format a number using Intl.NumberFormat, replacing numeral.js.
 * Supports numeral-style format strings: '0,0.00' and '0,0.00%'.
 */
const formatNumeral = (value: number, format: string = '0,0.00', locale: string = 'de'): string => {
  const isPercent = format.endsWith('%');
  const decimalMatch = format.replace(/%$/, '').match(/\.(\d+)/);
  const fractionDigits = decimalMatch ? decimalMatch[1].length : 0;
  const useGrouping = format.includes(',');

  if (isPercent) {
    // numeral '0,0.00%' multiplies by 100 and appends %
    // Intl style:'percent' does the same
    const formatter = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping,
    });
    return formatter.format(value);
  }

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping,
  });
  return formatter.format(value);
};

type ClassValue = string | number | boolean | undefined | null | Record<string, unknown> | ClassValue[];

const cn = (...args: ClassValue[]): string => {
  const classes: string[] = [];
  for (const arg of args) {
    if (!arg) continue;
    if (typeof arg === 'string' || typeof arg === 'number') {
      classes.push(String(arg));
    } else if (Array.isArray(arg)) {
      const inner = cn(...arg);
      if (inner) classes.push(inner);
    } else if (typeof arg === 'object') {
      for (const key of Object.keys(arg)) {
        if (arg[key]) classes.push(key);
      }
    }
  }
  return classes.join(' ');
};

export { isEqual, last, groupBy, round, debounce, throttle, omit, max, escapeStringRegexp, cn, downloadBlob, formatNumeral };
